import * as React from 'react';
import {
    Box, Card, CardContent, Chip, Collapse, Grid, Link as MuiLink, Stack, Table, TableBody, TableCell, TableHead, TableRow,
    Typography, Button,
} from '@mui/material';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import BuildIcon from '@mui/icons-material/Build';
import { Link as RouterLink } from 'react-router-dom';
import { AiRunDetailDto, AiRunStepDto, AiRunSummaryDto } from '../../models/dtos/AiDto';
import {
    AiRunStatusChip, formatAiCount, formatAiDate, formatAiDuration, formatAiMs, formatAiUsd, JsonBlock, StatLine, TextBlock,
} from './AiShared';

export function RunLink({ runId, short = true }: { runId: string; short?: boolean }) {
    return (
        <MuiLink component={RouterLink} to={`/ai/runs/${encodeURIComponent(runId)}`} underline="hover"
            sx={{ fontFamily: 'monospace', fontSize: 12 }}>
            {short ? runId.slice(0, 8) + '…' : runId}
        </MuiLink>
    );
}

function Collapsible({ label, children, defaultOpen = false }: { label: string; children: React.ReactNode; defaultOpen?: boolean }) {
    const [open, setOpen] = React.useState(defaultOpen);
    return (
        <Box>
            <Button size="small" onClick={() => setOpen(o => !o)} sx={{ px: 0, minWidth: 0, textTransform: 'none' }}>
                {open ? '▾' : '▸'} {label}
            </Button>
            <Collapse in={open} unmountOnExit>{children}</Collapse>
        </Box>
    );
}

function StepCard({ step }: { step: AiRunStepDto }) {
    const isModel = step.type === 'MODEL_CALL';
    const borderColor = step.isError ? 'error.main' : isModel ? 'primary.main' : 'secondary.main';
    return (
        <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Stack alignItems="center" sx={{ pt: 1 }}>
                <Box sx={{
                    width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    bgcolor: borderColor, color: 'common.white',
                }}>
                    {isModel ? <SmartToyIcon sx={{ fontSize: 16 }} /> : <BuildIcon sx={{ fontSize: 16 }} />}
                </Box>
                <Box sx={{ flex: 1, width: 2, bgcolor: 'divider', mt: 0.5 }} />
            </Stack>
            <Card variant="outlined" sx={{ flex: 1, mb: 1.5, borderLeft: 4, borderLeftColor: borderColor, minWidth: 0 }}>
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap" sx={{ mb: 1 }}>
                        <Typography variant="subtitle2">#{step.seq} {isModel ? 'Model call' : 'Tool call'}</Typography>
                        {isModel && step.model && <Chip size="small" label={step.model} />}
                        {isModel && step.stopReason && <Chip size="small" variant="outlined" label={`stop: ${step.stopReason}`} />}
                        {!isModel && step.kind && <Chip size="small" color="secondary" variant="outlined" label={step.kind} />}
                        {!isModel && step.tool && <Chip size="small" label={step.tool} sx={{ fontFamily: 'monospace' }} />}
                        {!isModel && step.target && <Typography variant="caption" color="text.secondary">→ {step.target}</Typography>}
                        {step.isError && <Chip size="small" color="error" label="error" />}
                        <Box sx={{ flex: 1 }} />
                        <Typography variant="caption" color="text.secondary">
                            {formatAiDate(step.at)} · {formatAiMs(step.latencyMs)}
                            {step.costUsd != null ? ` · ${formatAiUsd(step.costUsd)}` : ''}
                        </Typography>
                    </Stack>
                    {isModel && step.usage && (
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                            {formatAiCount(step.usage.input)} in · {formatAiCount(step.usage.output)} out
                            · cache read {formatAiCount(step.usage.cacheRead)} · cache write {formatAiCount(step.usage.cacheWrite)}
                        </Typography>
                    )}
                    {step.text && <TextBlock text={step.text} maxHeight={240} />}
                    {!isModel && (
                        <Stack gap={0.5} sx={{ mt: step.text ? 1 : 0 }}>
                            {step.childRunId && (
                                <Typography variant="body2">Child run: <RunLink runId={step.childRunId} short={false} /></Typography>
                            )}
                            <Collapsible label="Arguments" defaultOpen><JsonBlock value={step.args} maxHeight={240} /></Collapsible>
                            <Collapsible label={step.isError ? 'Error result' : 'Result'} defaultOpen={!!step.isError}>
                                <JsonBlock value={step.result} maxHeight={320} />
                            </Collapsible>
                        </Stack>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
}

export function AiRunStepsTimeline({ steps }: { steps: AiRunStepDto[] }) {
    if (steps.length === 0) return <Typography variant="body2" color="text.secondary">No steps recorded.</Typography>;
    const sorted = steps.slice().sort((a, b) => a.seq - b.seq);
    return <Box>{sorted.map(step => <StepCard key={step.seq} step={step} />)}</Box>;
}

export function AiRunChildrenTable({ runs }: { runs: AiRunSummaryDto[] }) {
    if (runs.length === 0) return <Typography variant="body2" color="text.secondary">No child runs.</Typography>;
    return (
        <Table size="small">
            <TableHead>
                <TableRow>
                    <TableCell>Run</TableCell>
                    <TableCell>Agent</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Turns</TableCell>
                    <TableCell>Cost</TableCell>
                    <TableCell>Duration</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {runs.map(c => (
                    <TableRow key={c.runId}>
                        <TableCell><RunLink runId={c.runId} /></TableCell>
                        <TableCell>{c.agentCode}</TableCell>
                        <TableCell><AiRunStatusChip status={c.status} /></TableCell>
                        <TableCell>{c.turns}</TableCell>
                        <TableCell>{formatAiUsd(c.costUsd)}</TableCell>
                        <TableCell>{formatAiDuration(c.startedAt, c.finishedAt)}</TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    );
}

/** Conversation-mode runs store `{ conversationId, … }` as their input. */
function conversationId(run: AiRunDetailDto): string | null {
    const input = run.input as { conversationId?: unknown } | null;
    return input && typeof input.conversationId === 'string' ? input.conversationId : null;
}

/** Full run: summary, input / output, steps timeline and child runs. */
export default function AiRunDetailView({ run }: { run: AiRunDetailDto }) {
    return (
        <Stack gap={2}>
            <Grid container spacing={2}>
                <Grid item xs={12} md={5}>
                    <Card variant="outlined" sx={{ height: '100%' }}>
                        <CardContent>
                            <Stack gap={0.75}>
                                <StatLine label="Status" value={<AiRunStatusChip status={run.status} />} />
                                <StatLine label="Agent" value={
                                    <MuiLink component={RouterLink} to={`/ai/agents/${encodeURIComponent(run.agentCode)}`} underline="hover">{run.agentCode}</MuiLink>
                                } />
                                <StatLine label="Caller" value={run.caller} />
                                <StatLine label="End user" value={run.endUserId != null
                                    ? <MuiLink component={RouterLink} to={`/users/${run.endUserId}`} underline="hover">#{run.endUserId}</MuiLink>
                                    : '—'} />
                                <StatLine label="Subject" value={run.subjectType ? `${run.subjectType} / ${run.subjectId ?? '—'}` : '—'} />
                                {conversationId(run) && (
                                    <StatLine label="Conversation" value={
                                        <MuiLink component={RouterLink} to={`/ai/conversations/${encodeURIComponent(conversationId(run)!)}`} underline="hover">open transcript</MuiLink>
                                    } />
                                )}
                                <StatLine label="Turns" value={run.turns} />
                                <StatLine label="Task executions" value={
                                    <MuiLink component={RouterLink} to={`/ai/task-executions?runId=${encodeURIComponent(run.runId)}`} underline="hover">task tools of this run</MuiLink>
                                } />
                                <StatLine label="Cost" value={formatAiUsd(run.costUsd)} />
                                <StatLine label="Depth" value={run.depth} />
                                {run.parentRunId && <StatLine label="Parent run" value={<RunLink runId={run.parentRunId} />} />}
                                {run.rootRunId && run.rootRunId !== run.runId && <StatLine label="Root run" value={<RunLink runId={run.rootRunId} />} />}
                                <StatLine label="Created" value={formatAiDate(run.createdAt)} />
                                <StatLine label="Started" value={formatAiDate(run.startedAt)} />
                                <StatLine label="Finished" value={formatAiDate(run.finishedAt)} />
                                <StatLine label="Duration" value={formatAiDuration(run.startedAt, run.finishedAt)} />
                            </Stack>
                            {run.error && (
                                <Box sx={{ mt: 1.5 }}>
                                    <Typography variant="subtitle2" color="error">Error</Typography>
                                    <TextBlock text={run.error} />
                                </Box>
                            )}
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} md={7}>
                    <Stack gap={1.5}>
                        <Box>
                            <Typography variant="subtitle2">Input</Typography>
                            <JsonBlock value={run.input} maxHeight={220} />
                        </Box>
                        <Box>
                            <Typography variant="subtitle2">Output</Typography>
                            {run.output != null ? <JsonBlock value={run.output} maxHeight={260} /> : <TextBlock text={run.outputText} maxHeight={260} />}
                        </Box>
                        {run.output != null && run.outputText && (
                            <Collapsible label="Output text"><TextBlock text={run.outputText} /></Collapsible>
                        )}
                    </Stack>
                </Grid>
            </Grid>
            <Box>
                <Typography variant="h6" sx={{ mb: 1 }}>Steps ({run.steps?.length ?? 0})</Typography>
                <AiRunStepsTimeline steps={run.steps ?? []} />
            </Box>
            <Box>
                <Typography variant="h6" sx={{ mb: 1 }}>Child runs ({run.children?.length ?? 0})</Typography>
                <AiRunChildrenTable runs={run.children ?? []} />
            </Box>
        </Stack>
    );
}
